import { IError } from '@axmit/error-helper';
import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { AppDispatch } from 'app/store';
import {
  IBitecoinCustomerInfoModel,
  IBitecoinRewardCategory,
  IBitecoinRewardModel,
  TGroupedBitecoinRewardCollection,
} from 'entities/Bitecoin/Bitecoin.models';
import { bitecoinTransport } from 'entities/Bitecoin/Bitecoin.transport';

export interface IState {
  // bitecoin customer info
  bitecoinCustomerInfoModel: IBitecoinCustomerInfoModel | null;
  bitecoinCustomerInfoModelLoading: boolean;
  // rewards
  bitecoinRewardCollectionLoading: boolean;
  bitecoinRewardCollection: IBitecoinRewardModel[] | null;
  bitecoinGroupedRewardCollection: TGroupedBitecoinRewardCollection | null;

  bitecoinRewardModelLoading: boolean;
  bitecoinRewardModel: IBitecoinRewardModel | null;
  bitecoinRewardModelError: IError | null;
}

const initialState: IState = {
  // bitecoin customer info
  bitecoinCustomerInfoModel: null,
  bitecoinCustomerInfoModelLoading: false,
  // rewards
  bitecoinRewardCollectionLoading: false,
  bitecoinRewardCollection: null,
  bitecoinGroupedRewardCollection: null,

  bitecoinRewardModelLoading: false,
  bitecoinRewardModel: null,
  bitecoinRewardModelError: null,
};

export const bitecoinSlice = createSlice({
  name: 'bitecoin',
  initialState,
  reducers: {
    // bitecoin customer info
    setBitecoinCustomerInfoModel(state, action: PayloadAction<IBitecoinCustomerInfoModel | null>) {
      state.bitecoinCustomerInfoModel = action.payload;
    },
    setBitecoinCustomerInfoModelLoading(state, action: PayloadAction<boolean>) {
      state.bitecoinCustomerInfoModelLoading = action.payload;
    },
    // reward collection
    setBitecoinRewardCollectionLoading(state, action: PayloadAction<boolean>) {
      state.bitecoinRewardCollectionLoading = action.payload;
    },
    setBitecoinRewardCollection(state, action: PayloadAction<IBitecoinRewardModel[] | null>) {
      state.bitecoinRewardCollection = action.payload;
    },
    setBitecoinGroupedRewardCollection(state, action: PayloadAction<TGroupedBitecoinRewardCollection | null>) {
      state.bitecoinGroupedRewardCollection = action.payload;
    },

    // reward model
    setBitecoinRewardModelLoading(state, action: PayloadAction<boolean>) {
      state.bitecoinRewardModelLoading = action.payload;
    },
    setBitecoinRewardModel(state, action: PayloadAction<IBitecoinRewardModel | null>) {
      state.bitecoinRewardModel = action.payload;
    },
    setBitecoinRewardModelError(state, action: PayloadAction<IError | null>) {
      state.bitecoinRewardModelError = action.payload;
    },
  },
});

export const {
  // rewards
  setBitecoinRewardCollectionLoading,
  setBitecoinRewardCollection,
  setBitecoinGroupedRewardCollection,
  setBitecoinCustomerInfoModel,
  setBitecoinCustomerInfoModelLoading,
} = bitecoinSlice.actions;

export default bitecoinSlice.reducer;

export const getBitecoinCustomerInfo = () => {
  async function thunk(dispatch: AppDispatch) {
    dispatch(setBitecoinCustomerInfoModelLoading(true));

    try {
      const bitecoinCustomerInfoModel = await bitecoinTransport.getBitecoinCustomerInfo();

      dispatch(setBitecoinCustomerInfoModel(bitecoinCustomerInfoModel));
    } catch (error) {
      console.log('getBitecoinCustomerInfo error', error);
    } finally {
      dispatch(setBitecoinCustomerInfoModelLoading(false));
    }
  }

  return thunk;
};

export const getBitecoinRewardCollectionByStoreId = (storeId: string) => {
  async function thunk(dispatch: AppDispatch) {
    dispatch(setBitecoinRewardCollectionLoading(true));

    try {
      const bitecoinRewardCollection = await bitecoinTransport.getBitecoinRewardCollectionByStoreId(storeId);
      const groupedByCategories = Object.values(
        bitecoinRewardCollection.reduce(
          (acc, reward) => {
            const categoryId = reward.category.id;

            if (!acc[categoryId]) {
              acc[categoryId] = {
                category: reward.category,
                rewards: [],
              };
            }

            acc[categoryId].rewards.push(reward);

            return acc;
          },
          {} as Record<string, { category: IBitecoinRewardCategory; rewards: IBitecoinRewardModel[] }>,
        ),
      );

      dispatch(setBitecoinRewardCollection(bitecoinRewardCollection));
      dispatch(setBitecoinGroupedRewardCollection(groupedByCategories));
    } catch (error) {
      console.log('getBitecoinRewardCollectionByStoreId error', error);
    } finally {
      dispatch(setBitecoinRewardCollectionLoading(false));
    }
  }
  return thunk;
};

export const getBitecoinRewardCollectionDefault = () => {
  async function thunk(dispatch: AppDispatch) {
    dispatch(setBitecoinRewardCollectionLoading(true));

    try {
      const bitecoinRewardCollection = await bitecoinTransport.getBitecoinRewardCollectionDefault();
      const groupedByCategories = Object.values(
        bitecoinRewardCollection.reduce(
          (acc, reward) => {
            const categoryId = reward.category.id;

            if (!acc[categoryId]) {
              acc[categoryId] = {
                category: reward.category,
                rewards: [],
              };
            }

            acc[categoryId].rewards.push(reward);

            return acc;
          },
          {} as Record<string, { category: IBitecoinRewardCategory; rewards: IBitecoinRewardModel[] }>,
        ),
      );

      dispatch(setBitecoinRewardCollection(bitecoinRewardCollection));
      dispatch(setBitecoinGroupedRewardCollection(groupedByCategories));
    } catch (error) {
      console.log('getBitecoinRewardCollectionDefault error', error);
    } finally {
      dispatch(setBitecoinRewardCollectionLoading(false));
    }
  }
  return thunk;
};
